import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CategoryForm } from '@/components/categories/CategoryForm'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Category } from '@/types/api'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

type CategoryFormData = {
  name: string
  description?: string
  status: 'active' | 'inactive'
  sortOrder: number
  seoTitle?: string
  seoDescription?: string
  seoKeywords?: string
}

const CategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [category, setCategory] = useState<Category | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadCategory = useCallback(async () => {
    if (!id) return

    setIsLoading(true)
    try {
      const response = await apiClient.getCategory(id)
      setCategory(response.data)
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load category. Please try again.',
        variant: 'destructive',
      })
      navigate('/categories')
    } finally {
      setIsLoading(false)
    }
  }, [id, toast, navigate])

  useEffect(() => {
    loadCategory()
  }, [loadCategory])

  const handleFormSubmit = async (data: CategoryFormData) => {
    if (!id) return

    setIsSubmitting(true)
    try {
      const payload = {
        name: data.name,
        description: data.description || undefined,
        is_active: data.status === 'active',
        display_order: data.sortOrder,
        meta_title: data.seoTitle || undefined,
        meta_description: data.seoDescription || undefined,
        meta_keywords: data.seoKeywords || undefined,
      }

      await apiClient.updateCategory(id, payload)

      toast({
        title: 'Success',
        description: 'Category updated successfully',
      })

      navigate('/categories')
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save category. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/categories')
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/categories')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Categories
          </Button>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Category Details</h1>
              <p className="text-muted-foreground">View and edit category information</p>
            </div>
          </div>
        </div>
        
        <div className="text-center py-12">
          <div className="text-muted-foreground">Loading category...</div>
        </div>
      </div>
    )
  }

  if (!category) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/categories')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Categories
          </Button>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold">Category Details</h1>
              <p className="text-muted-foreground">View and edit category information</p>
            </div>
          </div>
        </div>
        
        <div className="text-center py-12">
          <div className="text-muted-foreground">Category not found</div>
        </div>
      </div>
    )
  }

  const categoryWithExtras = category as Category & {
    is_active?: boolean
    sort_order?: number
    display_order?: number
    meta_title?: string
    meta_description?: string
    meta_keywords?: string
    seo?: {
      meta_title?: string
      meta_description?: string
      meta_keywords?: string
    }
  }

  const initialData: Partial<CategoryFormData> = {
    name: category.name,
    description: category.description || '',
    status: category.status || (categoryWithExtras.is_active ? 'active' : 'inactive'),
    sortOrder: categoryWithExtras.sort_order ?? categoryWithExtras.display_order ?? 0,
    seoTitle: categoryWithExtras.meta_title || categoryWithExtras.seo?.meta_title || '',
    seoDescription: categoryWithExtras.meta_description || categoryWithExtras.seo?.meta_description || '',
    seoKeywords: categoryWithExtras.meta_keywords || categoryWithExtras.seo?.meta_keywords || '',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/categories')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Categories
        </Button>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Category Details</h1>
            <p className="text-muted-foreground">View and edit category information</p>
          </div>
        </div>
      </div>

      <CategoryForm
        initialData={initialData}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default CategoryDetailPage

